import Phaser from 'phaser'

import type { LoungeLayout, RoomLayout } from '~/game/world'

// The office environment: warm-studio materials (wood / carpet / stone tile),
// corridors joining the rooms into one building, and the building shell.
// EVERYTHING here derives from the FINAL room rects at draw time — arrange-mode
// drags land in computeLayout before we run, and the scene fully redraws on
// restart, so the environment re-flows automatically when rooms move.

export const ENV_TEX = {
  wood: 'env-wood',
  carpet: 'env-carpet',
  lobby: 'env-lobby',
  pavement: 'env-pavement',
  slate: 'env-slate',
  steel: 'env-steel',
  coolLobby: 'env-cool-lobby',
  coolPavement: 'env-cool-pavement',
  circuit: 'env-circuit',
  mat: 'env-mat',
  planter: 'env-planter',
  bench: 'env-bench',
  tree: 'env-tree',
  bush: 'env-bush',
  bikeRack: 'env-bike-rack',
  catStand: 'env-cat-stand',
  catSit: 'env-cat-sit',
} as const

// --- Office style templates (picked in Arrange mode, saved per org) -------------

export type OfficeStyle = 'warm' | 'cool' | 'mix'

export interface StyleSpec {
  label: string
  /** Floor materials by room category. */
  team: string
  meeting: string
  lobby: string
  /** Corridor / street paving. */
  pavement: string
  /** Building shell tones. */
  wall: number
  wallHi: number
  ground: number
  apron: number
  frame: number
}

export const STYLES: Record<OfficeStyle, StyleSpec> = {
  warm: {
    label: 'Warm studio',
    team: ENV_TEX.wood,
    meeting: ENV_TEX.carpet,
    lobby: ENV_TEX.lobby,
    pavement: ENV_TEX.pavement,
    wall: 0x332a22,
    wallHi: 0x4a3d30,
    ground: 0x121010,
    apron: 0x141110,
    frame: 0x3a332b,
  },
  cool: {
    label: 'Glass tech',
    team: ENV_TEX.slate,
    meeting: ENV_TEX.steel,
    lobby: ENV_TEX.coolLobby,
    pavement: ENV_TEX.coolPavement,
    wall: 0x272e40,
    wallHi: 0x3c4660,
    ground: 0x0f1118,
    apron: 0x12141c,
    frame: 0x2c3245,
  },
  mix: {
    label: 'Warm + tech',
    // Human spaces stay warm; system spaces (meetings, corridors) go cool.
    team: ENV_TEX.wood,
    meeting: ENV_TEX.steel,
    lobby: ENV_TEX.lobby,
    pavement: ENV_TEX.coolPavement,
    wall: 0x332a22,
    wallHi: 0x4a3d30,
    ground: 0x111011,
    apron: 0x131113,
    frame: 0x3a332b,
  },
}

const STYLE_KEY = 'glass-office.officeStyle.'

export function getOfficeStyle(orgId: string): OfficeStyle {
  try {
    const v = localStorage.getItem(STYLE_KEY + orgId)
    if (v === 'cool' || v === 'mix' || v === 'warm') return v
  } catch {
    // Storage unavailable — default below.
  }
  return 'warm'
}

export function setOfficeStyle(orgId: string, style: OfficeStyle): void {
  try {
    localStorage.setItem(STYLE_KEY + orgId, style)
  } catch {
    // Best effort.
  }
}

/** Same clock the night overlay uses — windows show the matching sky. */
export function skyPhase(): 'day' | 'dusk' | 'night' {
  const hour = new Date().getHours()
  if (hour >= 20 || hour < 6) return 'night'
  if (hour >= 18 || hour < 8) return 'dusk'
  return 'day'
}

/** Bake the tiny pixel material tiles once per scene create. */
export function buildEnvironmentTextures(scene: Phaser.Scene): void {
  for (const key of Object.values(ENV_TEX)) {
    if (scene.textures.exists(key)) scene.textures.remove(key)
  }

  // Wood planks (team rooms): warm browns, horizontal boards, staggered seams.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x2b241d, 1)
    g.fillRect(0, 0, 28, 28)
    g.fillStyle(0x332a21, 1)
    for (let row = 0; row < 4; row++) {
      g.fillRect(0, row * 7, 28, 1)
    }
    // Staggered vertical seams per plank row.
    g.fillStyle(0x221c16, 1)
    g.fillRect(9, 0, 1, 7)
    g.fillRect(21, 7, 1, 7)
    g.fillRect(4, 14, 1, 7)
    g.fillRect(16, 21, 1, 7)
    // A couple of grain flecks.
    g.fillStyle(0x3a2f24, 1)
    g.fillRect(14, 3, 3, 1)
    g.fillRect(6, 17, 2, 1)
    g.fillRect(22, 24, 3, 1)
    g.generateTexture(ENV_TEX.wood, 28, 28)
    g.destroy()
  }

  // Carpet (meeting rooms): tight woven dots on a muted warm base.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x272120, 1)
    g.fillRect(0, 0, 28, 28)
    g.fillStyle(0x2e2624, 1)
    for (let y = 0; y < 28; y += 4) {
      for (let x = (y / 4) % 2 === 0 ? 0 : 2; x < 28; x += 4) {
        g.fillRect(x, y, 1, 1)
      }
    }
    g.fillStyle(0x221c1b, 1)
    for (let y = 2; y < 28; y += 8) {
      for (let x = 5; x < 28; x += 9) g.fillRect(x, y, 1, 1)
    }
    g.generateTexture(ENV_TEX.carpet, 28, 28)
    g.destroy()
  }

  // Lobby/commons: warm checker tile.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x252028, 1)
    g.fillRect(0, 0, 28, 28)
    g.fillStyle(0x211c23, 1)
    g.fillRect(0, 0, 14, 14)
    g.fillRect(14, 14, 14, 14)
    g.lineStyle(1, 0x1c1820, 1)
    g.strokeRect(0, 0, 28, 28)
    g.generateTexture(ENV_TEX.lobby, 28, 28)
    g.destroy()
  }

  // Corridor pavement: warm stone with seams + chips.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x1f1b17, 1)
    g.fillRect(0, 0, 24, 24)
    g.lineStyle(1, 0x17130f, 1)
    g.strokeRect(0, 0, 24, 12)
    g.strokeRect(-12, 12, 24, 12)
    g.strokeRect(12, 12, 24, 12)
    g.fillStyle(0x262019, 1)
    g.fillRect(4, 3, 2, 1)
    g.fillRect(17, 8, 2, 1)
    g.fillRect(9, 17, 2, 1)
    g.generateTexture(ENV_TEX.pavement, 24, 24)
    g.destroy()
  }

  // Slate tile (cool team rooms): large blue-grey tiles, grout + glints.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x232733, 1)
    g.fillRect(0, 0, 28, 28)
    g.lineStyle(1, 0x1b1f28, 1)
    g.strokeRect(0, 0, 14, 14)
    g.strokeRect(14, 14, 14, 14)
    g.strokeRect(14, 0, 14, 14)
    g.strokeRect(0, 14, 14, 14)
    g.fillStyle(0x2c3140, 1)
    g.fillRect(2, 2, 4, 1)
    g.fillRect(18, 16, 4, 1)
    g.fillStyle(0x7ff0ff, 0.06)
    g.fillRect(8, 8, 2, 2)
    g.generateTexture(ENV_TEX.slate, 28, 28)
    g.destroy()
  }

  // Brushed steel (cool meeting rooms): fine horizontal grain.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x2a2f3a, 1)
    g.fillRect(0, 0, 28, 28)
    g.fillStyle(0x323847, 1)
    for (let y = 2; y < 28; y += 5) g.fillRect(0, y, 28, 1)
    g.fillStyle(0x242833, 1)
    for (let y = 4; y < 28; y += 9) g.fillRect(0, y, 28, 1)
    g.generateTexture(ENV_TEX.steel, 28, 28)
    g.destroy()
  }

  // Cool lobby checker: slate blues.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x232838, 1)
    g.fillRect(0, 0, 28, 28)
    g.fillStyle(0x1e2330, 1)
    g.fillRect(0, 0, 14, 14)
    g.fillRect(14, 14, 14, 14)
    g.lineStyle(1, 0x191d28, 1)
    g.strokeRect(0, 0, 28, 28)
    g.generateTexture(ENV_TEX.coolLobby, 28, 28)
    g.destroy()
  }

  // Circuit floor (Agent Bay): near-black navy raised floor with faint traces
  // and the occasional live node — a machine room you'd want to live in.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x161826, 1)
    g.fillRect(0, 0, 28, 28)
    // Raised-floor panel seams.
    g.lineStyle(1, 0x101220, 1)
    g.strokeRect(0, 0, 28, 28)
    g.strokeRect(0, 0, 14, 14)
    g.strokeRect(14, 14, 14, 14)
    // Circuit traces: thin L-shaped runs.
    g.fillStyle(0x272b45, 1)
    g.fillRect(4, 6, 9, 1)
    g.fillRect(12, 6, 1, 7)
    g.fillRect(18, 20, 7, 1)
    g.fillRect(18, 15, 1, 6)
    g.fillRect(2, 22, 6, 1)
    // Live nodes: one violet, one cyan, barely-there glow.
    g.fillStyle(0x8b7ff0, 0.5)
    g.fillRect(12, 5, 2, 2)
    g.fillStyle(0x7ff0ff, 0.35)
    g.fillRect(18, 20, 2, 2)
    g.generateTexture(ENV_TEX.circuit, 28, 28)
    g.destroy()
  }

  // Cool pavement: dark blue-grey stones with an indigo fleck.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x1c1f28, 1)
    g.fillRect(0, 0, 24, 24)
    g.lineStyle(1, 0x151821, 1)
    g.strokeRect(0, 0, 24, 12)
    g.strokeRect(-12, 12, 24, 12)
    g.strokeRect(12, 12, 24, 12)
    g.fillStyle(0x262b38, 1)
    g.fillRect(5, 4, 2, 1)
    g.fillRect(16, 9, 2, 1)
    g.fillStyle(0x5b54c8, 0.25)
    g.fillRect(10, 17, 2, 1)
    g.generateTexture(ENV_TEX.coolPavement, 24, 24)
    g.destroy()
  }

  // Door mat: terracotta with a border weave.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x8a4a32, 1)
    g.fillRect(0, 0, 18, 7)
    g.fillStyle(0xa25a3c, 1)
    g.fillRect(1, 1, 16, 5)
    g.fillStyle(0x8a4a32, 1)
    for (let x = 3; x < 16; x += 4) g.fillRect(x, 3, 2, 1)
    g.generateTexture(ENV_TEX.mat, 18, 7)
    g.destroy()
  }

  // A tree for outside the building: trunk + layered canopy.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x4a3524, 1)
    g.fillRect(8, 16, 4, 8)
    g.fillStyle(0x2e5c38, 1)
    g.fillRect(3, 6, 14, 10)
    g.fillRect(5, 2, 10, 6)
    g.fillStyle(0x3d7a48, 1)
    g.fillRect(5, 4, 7, 4)
    g.fillRect(4, 9, 5, 4)
    g.fillRect(12, 8, 4, 4)
    g.fillStyle(0x25492d, 1)
    g.fillRect(13, 12, 4, 3)
    g.fillRect(6, 13, 4, 2)
    g.generateTexture(ENV_TEX.tree, 20, 24)
    g.destroy()
  }

  // A low bush.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x2e5c38, 1)
    g.fillRect(1, 2, 10, 5)
    g.fillRect(3, 0, 6, 4)
    g.fillStyle(0x3d7a48, 1)
    g.fillRect(3, 1, 4, 2)
    g.fillRect(7, 3, 3, 2)
    g.generateTexture(ENV_TEX.bush, 12, 7)
    g.destroy()
  }

  // Bike rack by the entrance: frame + two parked bikes.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x565c68, 1)
    g.fillRect(0, 2, 2, 10)
    g.fillRect(26, 2, 2, 10)
    g.fillRect(0, 2, 28, 2)
    // Bikes: wheels + a hint of frame.
    g.lineStyle(1, 0x8b93a7, 1)
    g.strokeCircle(7, 9, 3)
    g.strokeCircle(13, 9, 3)
    g.strokeCircle(18, 9, 3)
    g.strokeCircle(24, 9, 3)
    g.lineStyle(1, 0xb5654a, 1)
    g.lineBetween(7, 9, 10, 5)
    g.lineBetween(10, 5, 13, 9)
    g.lineStyle(1, 0x4a7a5c, 1)
    g.lineBetween(18, 9, 21, 5)
    g.lineBetween(21, 5, 24, 9)
    g.generateTexture(ENV_TEX.bikeRack, 28, 13)
    g.destroy()
  }

  // Corridor planter: terracotta pot + a leafy bush.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x2e5c38, 1)
    g.fillRect(2, 2, 10, 6)
    g.fillStyle(0x3d7a48, 1)
    g.fillRect(3, 0, 8, 5)
    g.fillRect(1, 4, 4, 3)
    g.fillRect(9, 4, 4, 3)
    g.fillStyle(0x8a4a32, 1)
    g.fillRect(2, 8, 10, 6)
    g.fillStyle(0xa25a3c, 1)
    g.fillRect(2, 8, 10, 2)
    g.generateTexture(ENV_TEX.planter, 14, 14)
    g.destroy()
  }

  // Entrance bench: warm wood slats on dark legs.
  {
    const g = scene.add.graphics()
    g.fillStyle(0x1c1712, 1)
    g.fillRect(2, 6, 3, 5)
    g.fillRect(21, 6, 3, 5)
    g.fillStyle(0x6b4a2e, 1)
    g.fillRect(0, 2, 26, 4)
    g.fillStyle(0x7d5836, 1)
    g.fillRect(0, 2, 26, 1)
    g.fillRect(0, 4, 26, 1)
    g.generateTexture(ENV_TEX.bench, 26, 11)
    g.destroy()
  }

  // The office cat: a little orange tabby (standing + sitting poses).
  {
    const g = scene.add.graphics()
    // Standing: body, head, ears, tail, legs.
    g.fillStyle(0xc98346, 1)
    g.fillRect(2, 3, 8, 4) // body
    g.fillRect(9, 1, 4, 4) // head
    g.fillStyle(0xa5652f, 1)
    g.fillRect(9, 0, 1, 1) // ear
    g.fillRect(12, 0, 1, 1) // ear
    g.fillRect(4, 4, 1, 2) // stripe
    g.fillRect(6, 3, 1, 2) // stripe
    g.fillRect(0, 1, 2, 3) // tail up
    g.fillStyle(0xc98346, 1)
    g.fillRect(1, 3, 1, 1)
    g.fillStyle(0xe8e6df, 1)
    g.fillRect(10, 4, 2, 1) // chest
    g.fillStyle(0xa5652f, 1)
    g.fillRect(3, 7, 1, 2)
    g.fillRect(8, 7, 1, 2) // legs
    g.generateTexture(ENV_TEX.catStand, 13, 9)
    g.destroy()
  }
  {
    const g = scene.add.graphics()
    // Sitting: upright loaf, tail wrapped.
    g.fillStyle(0xc98346, 1)
    g.fillRect(1, 3, 6, 5) // body
    g.fillRect(2, 0, 4, 4) // head
    g.fillStyle(0xa5652f, 1)
    g.fillRect(2, 0, 1, 1)
    g.fillRect(5, 0, 1, 1) // ears
    g.fillRect(2, 4, 1, 2)
    g.fillRect(4, 5, 1, 2) // stripes
    g.fillRect(6, 7, 3, 1) // tail wrap
    g.fillStyle(0xe8e6df, 1)
    g.fillRect(3, 3, 2, 1) // chest
    g.generateTexture(ENV_TEX.catSit, 9, 8)
    g.destroy()
  }
}

export interface Walkways {
  /** Horizontal street rects (one per room row) + the vertical spine. */
  streets: Array<{ x: number; y: number; w: number; h: number }>
  /** Short paths from each room's door down to its street. */
  stubs: Array<{ x: number; y: number; w: number; h: number }>
  /** Door mats at each room's entrance (centre-bottom). */
  mats: Array<{ x: number; y: number }>
}

const STREET_H = 16
const SPINE_W = 18

/**
 * Compute the corridor network from the FINAL room rects: rooms cluster into
 * rows (tolerant, so an arrange-dragged room simply forms/joins the nearest
 * cluster), each row gets a street below it, every door gets a stub + mat,
 * and one vertical spine ties the streets to the lounge.
 */
export function computeWalkways(
  rooms: RoomLayout[],
  lounge: LoungeLayout,
): Walkways {
  const streets: Walkways['streets'] = []
  const stubs: Walkways['stubs'] = []
  const mats: Walkways['mats'] = []
  if (rooms.length === 0) return { streets, stubs, mats }

  // Cluster rooms into rows by their top edge (same grid row = identical y;
  // dragged rooms cluster within a tolerance or become their own row).
  const sorted = [...rooms].sort((a, b) => a.y - b.y)
  const rows: RoomLayout[][] = []
  for (const room of sorted) {
    const row = rows.find((r) => Math.abs(r[0].y - room.y) < 70)
    if (row) row.push(room)
    else rows.push([room])
  }

  let minStreetY = Number.POSITIVE_INFINITY
  let maxStreetY = Number.NEGATIVE_INFINITY
  for (const row of rows) {
    const bottom = Math.max(...row.map((r) => r.y + r.h))
    const streetY = bottom + 12
    const left = Math.min(...row.map((r) => r.x)) - 16
    const right = Math.max(...row.map((r) => r.x + r.w)) + 16
    streets.push({ x: left, y: streetY, w: right - left, h: STREET_H })
    minStreetY = Math.min(minStreetY, streetY)
    maxStreetY = Math.max(maxStreetY, streetY)
    for (const room of row) {
      const doorX = room.x + room.w / 2
      const bottomY = room.y + room.h
      stubs.push({ x: doorX - 7, y: bottomY - 2, w: 14, h: streetY - bottomY + 4 })
      mats.push({ x: doorX, y: bottomY + 3 })
    }
  }

  // The spine: one vertical corridor from the first street down past the last,
  // reaching the lounge so the social band is ON the network.
  const minX = Math.min(...rooms.map((r) => r.x))
  const maxX = Math.max(...rooms.map((r) => r.x + r.w))
  const spineX = Math.round((minX + maxX) / 2 - SPINE_W / 2)
  const spineBottom = Math.max(maxStreetY + STREET_H, lounge.cy - 10)
  streets.push({
    x: spineX,
    y: minStreetY,
    w: SPINE_W,
    h: spineBottom - minStreetY,
  })

  return { streets, stubs, mats }
}

/** The building's outer bounds: rooms + lounge + breathing room. */
export function buildingBounds(
  rooms: RoomLayout[],
  lounge: LoungeLayout,
): { x: number; y: number; w: number; h: number } {
  const xs = rooms.map((r) => r.x)
  const ys = rooms.map((r) => r.y)
  const rights = rooms.map((r) => r.x + r.w)
  const bottoms = rooms.map((r) => r.y + r.h)
  // Racks stand left of the lounge — leave room for the machine corner.
  const minX = Math.min(...xs, lounge.cx - lounge.w / 2 - 190) - 34
  const minY = Math.min(...ys) - 34
  const maxX = Math.max(...rights, lounge.cx + lounge.w / 2 + 60) + 34
  const maxY = Math.max(...bottoms, lounge.cy + 60) + 34
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}
